import React from "react";
import PropTypes from "prop-types";
import { getImage } from "gatsby-plugin-image";

// Drop-in v2-compatible image renderer that uses gatsby-plugin-image v3's
// processed data (gatsbyImageData) but emits the SAME DOM structure as
// gatsby-image v2 — so the entire codebase's existing CSS targeting
// `.gatsby-image-wrapper` keeps working without modification.
//
// v2 structure (which our SCSS expects):
//   <div class="gatsby-image-wrapper">
//     <div style="padding-bottom: X%"></div>   <!-- aspect ratio spacer -->
//     <picture>
//       <source type="image/webp" srcSet="..." />
//       <img src="..." srcSet="..." style="position:absolute;width:100%;height:100%;object-fit:cover" />
//     </picture>
//   </div>
//
// v3's <GatsbyImage> emits an extra inner div with max-width / inline aspect
// ratio that conflicts with our SCSS — every component that styles
// .gatsby-image-wrapper had to be patched. This component avoids that entirely.

const PreviewCompatibleImage = ({ imageInfo, styles = {}, className = "" }) => {
  const { alt = "", title = "", childImageSharp, image } = imageInfo;

  // Path 1: image is a string (e.g., imported SVG)
  if (image && typeof image === "string") {
    return (
      <img
        style={{ borderRadius: "5px", ...styles }}
        className={className}
        src={image}
        alt={alt}
        title={title}
      />
    );
  }

  // Path 2/3: extract gatsbyImageData
  const data = getImage(image) || (childImageSharp ? getImage({ childImageSharp } as any) : null);
  if (!data) return null;

  const fallback = (data as any).images?.fallback || {};
  const sources = (data as any).images?.sources || [];
  // Base64 LQIP from gatsby-plugin-sharp `placeholder: BLURRED`.
  // Inline SVG/PNG, ~1-3KB, decodes instantly during initial paint.
  const rawPlaceholder = (data as any).placeholder?.fallback as string | undefined;
  // Blur-up tylko dla źródeł bez kanału alpha. sharp zachowuje format źródła
  // w LQIP, więc `data:image/jpeg` == źródło nieprzezroczyste, a `image/png` ==
  // potencjalnie wycięte tło (zdjęcia lekarzy, ikony ofert, ilustracje).
  // Przy przezroczystym PNG rozmyty placeholder prześwituje przez wycięcie
  // i tworzy ciemną poświatę wokół sylwetki — obrazek nigdy go nie zakryje.
  const placeholder =
    rawPlaceholder && rawPlaceholder.startsWith("data:image/jpeg")
      ? rawPlaceholder
      : undefined;
  const bgColor = (data as any).backgroundColor as string | undefined;
  const aspectPadding = data.height && data.width
    ? `${(data.height / data.width) * 100}%`
    : "100%";

  return (
    <div
      className={`gatsby-image-wrapper ${className}`}
      style={{
        position: "relative",
        overflow: "hidden",
        borderRadius: "5px",
        backgroundColor: bgColor,
        ...styles,
      }}
    >
      {/* aspect-ratio spacer (mirrors gatsby-image v2 padding-bottom hack) */}
      <div aria-hidden="true" style={{ width: "100%", paddingBottom: aspectPadding }} />
      {/* Placeholder layer: blurred LQIP shown during high-res image fetch.
          Background-image (not <img>) → SSR-friendly + no-JS friendly, no
          opacity transitions needed because the high-res <picture><img>
          below paints opaque on top once it loads — dlatego warstwa jest
          renderowana wyłącznie dla źródeł JPEG (patrz `placeholder` wyżej). */}
      {placeholder && (
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundImage: `url("${placeholder}")`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
          }}
        />
      )}
      <picture>
        {sources.map((s: any, i: number) => (
          <source key={i} srcSet={s.srcSet} sizes={s.sizes} type={s.type} />
        ))}
        <img
          src={fallback.src}
          srcSet={fallback.srcSet}
          sizes={fallback.sizes}
          alt={alt}
          title={title}
          decoding="async"
          // Intentionally NO loading="lazy": these wrappers are used for hero
          // illustrations, doctor cards, FAQ + offer cards, etc. — typically
          // above-the-fold sections. Native lazy + React 18 client-side nav
          // race condition meant images sometimes stayed empty until refresh.
          // Browser still optimizes loading; gatsby-remark-images keeps lazy
          // for blog inline images (where it's appropriate).
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition: "center",
          }}
        />
      </picture>
    </div>
  );
};

PreviewCompatibleImage.propTypes = {
  imageInfo: PropTypes.shape({
    alt: PropTypes.string,
    title: PropTypes.string,
    childImageSharp: PropTypes.object,
    image: PropTypes.oneOfType([PropTypes.object, PropTypes.string]).isRequired,
    style: PropTypes.object,
  }).isRequired,
};

export default PreviewCompatibleImage;
